import type { RgbaImage } from '../image';
import { cloneImage } from '../image';
import { areaById, LOOKS, type MockupJob, type MockupTemplate, type Placement, type PreparedMaps, type PrintArea } from './model';
import { quadToSquare, quadBounds } from './homography';
import { recolorGarment } from './recolor';
import { applyBackdrop, type ResolvedBackdrop } from './backdrop';

/** The placed art's rectangle in the area's own inch space. `'center'` on either axis is resolved
 *  HERE, against the size the art actually takes at `widthIn`, which is what makes a centred axis
 *  stay centred through a width change rather than needing the caller to re-derive the offset. */
export function artRectIn(area: PrintArea, placement: Placement, art: { width: number; height: number }): { x0: number; y0: number; w: number; h: number } {
  const w = placement.widthIn, h = (w * art.height) / art.width;
  const x0 = placement.xIn === 'center' ? (area.widthIn - w) / 2 : placement.xIn;
  const y0 = placement.yIn === 'center' ? (area.heightIn - h) / 2 : placement.yIn;
  return { x0, y0, w, h };
}
/** Bilinear sample at art-pixel coordinates (ax, ay) with pixel centres at +0.5; returns straight [r,g,b,a] in 0..1.
 *  The four taps are unrolled (no per-call closure) — this is the hottest path in renderMockup. */
export function sampleArt(art: RgbaImage, ax: number, ay: number): [number, number, number, number] {
  const w = art.width, h = art.height, d = art.data;
  const x = ax - 0.5, y = ay - 0.5;
  const x0 = Math.floor(x), y0 = Math.floor(y), fx = x - x0, fy = y - y0;
  const x1 = x0 + 1, y1 = y0 + 1;
  const w00 = (1 - fx) * (1 - fy), w10 = fx * (1 - fy), w01 = (1 - fx) * fy, w11 = fx * fy;
  let r = 0, g = 0, b = 0, a = 0;
  if (w00 > 0 && x0 >= 0 && y0 >= 0 && x0 < w && y0 < h) {
    const o = (y0 * w + x0) * 4, pa = (d[o + 3] / 255) * w00;
    r += (d[o] / 255) * pa; g += (d[o + 1] / 255) * pa; b += (d[o + 2] / 255) * pa; a += pa;
  }
  if (w10 > 0 && x1 >= 0 && y0 >= 0 && x1 < w && y0 < h) {
    const o = (y0 * w + x1) * 4, pa = (d[o + 3] / 255) * w10;
    r += (d[o] / 255) * pa; g += (d[o + 1] / 255) * pa; b += (d[o + 2] / 255) * pa; a += pa;
  }
  if (w01 > 0 && x0 >= 0 && y1 >= 0 && x0 < w && y1 < h) {
    const o = (y1 * w + x0) * 4, pa = (d[o + 3] / 255) * w01;
    r += (d[o] / 255) * pa; g += (d[o + 1] / 255) * pa; b += (d[o + 2] / 255) * pa; a += pa;
  }
  if (w11 > 0 && x1 >= 0 && y1 >= 0 && x1 < w && y1 < h) {
    const o = (y1 * w + x1) * 4, pa = (d[o + 3] / 255) * w11;
    r += (d[o] / 255) * pa; g += (d[o + 1] / 255) * pa; b += (d[o + 2] / 255) * pa; a += pa;
  }
  return a > 0 ? [r / a, g / a, b / a, a] : [0, 0, 0, 0];
}
/**
 * The axis-aligned bounding box (inches, in the area's space) of `rect` turned `rotationDeg` about
 * its own centre — what "does this print still fit the area?" has to ask once a print can turn.
 * A 12 × 16 in rect at 90° is 16 × 12 in about the same centre, which no 12 in-wide area holds.
 */
export function rotatedBounds(rect: { x0: number; y0: number; w: number; h: number }, rotationDeg: number): { x0: number; y0: number; w: number; h: number } {
  if (rotationDeg === 0) return { x0: rect.x0, y0: rect.y0, w: rect.w, h: rect.h };
  const th = (rotationDeg * Math.PI) / 180, c = Math.abs(Math.cos(th)), s = Math.abs(Math.sin(th));
  const w = rect.w * c + rect.h * s, h = rect.w * s + rect.h * c;
  return { x0: rect.x0 + rect.w / 2 - w / 2, y0: rect.y0 + rect.h / 2 - h / 2, w, h };
}
/**
 * Does a placed print run outside the area it is in? THE clip test — the one the
 * compositor reports as `clipped` and the one the web panel's `printFits` warns on
 * before a render has landed, so the amber line under Width and the footer's
 * "Clipped" cannot disagree about a print by a thousandth of an inch.
 *
 * The rect is passed in rather than derived, because the compositor needs it anyway
 * (it samples the art through it) and deriving it twice is how the two copies of
 * this inequality drifted apart in the first place.
 *
 * The 1e-9 slack is one float's worth: `artRectIn` divides by two for a centred
 * axis, so a print centred in an area exactly its own size lands a few ulps outside
 * it, and reporting that as clipped would flag the app's own "Centre in area".
 */
export function printClipped(area: { widthIn: number; heightIn: number }, rect: { x0: number; y0: number; w: number; h: number }, rotationDeg: number): boolean {
  const rb = rotatedBounds(rect, rotationDeg);
  return rb.x0 < -1e-9 || rb.y0 < -1e-9 || rb.x0 + rb.w > area.widthIn + 1e-9 || rb.y0 + rb.h > area.heightIn + 1e-9;
}
/** A bad placement fails silently otherwise: a negative widthIn draws a mirrored sliver and still
 *  reports clipped: false, 0/NaN draws nothing at all, Infinity reports clipped with no output —
 *  and a NaN rotation would blank the print the same way. */
function checkPrint(pl: Placement, rotationDeg: number): void {
  if (!(Number.isFinite(pl.widthIn) && pl.widthIn > 0) || !(pl.yIn === 'center' || Number.isFinite(pl.yIn)) || !(pl.xIn === 'center' || Number.isFinite(pl.xIn)) || !Number.isFinite(rotationDeg)) throw new Error('mockup: invalid placement');
}
export interface RenderInput {
  photo: RgbaImage; maps: PreparedMaps; template: MockupTemplate; job: MockupJob; art: RgbaImage;
  /** Art for the job's extra prints, keyed by `MockupPrint.docId` — or `'self'` for a print that
   *  carries `docId: null` and would otherwise fall back to `art`. */
  extraArts?: Record<string, RgbaImage>;
  /** The job's backdrop, resolved by the caller (a scene decoded and covered to the photo size).
   *  IGNORED when `maps.shadow` is absent: a non-separable template renders with its own backdrop
   *  rather than erroring, so switching the job back to a flat-lay brings the swap back. */
  backdrop?: ResolvedBackdrop | null;
}
export function renderMockup(input: RenderInput): { image: RgbaImage; clipped: boolean; skipped: string[] } {
  const { photo, maps, template, job, art, extraArts } = input;
  if (maps.width !== photo.width || maps.height !== photo.height) throw new Error('mockup: maps do not match the photo size');
  // Older callers (and anything read back from storage before 3C-1) carry neither field.
  const rotationDeg = job.rotationDeg ?? 0;
  checkPrint(job.placement, rotationDeg);
  const W = photo.width, H = photo.height;
  const base = job.garment ? recolorGarment(photo, maps.mask, template.garmentColor, job.garment) : cloneImage(photo);
  // Backdrop before the prints, over the recoloured (and rounded) base: the prints then land on top
  // of it exactly as they do on the photo's own backdrop.
  if (input.backdrop && maps.shadow) applyBackdrop(base, maps, input.backdrop);
  const look = LOOKS[job.look];
  const wrinkle = job.wrinkle ?? template.shading.wrinkle, texture = job.texture ?? template.shading.texture;
  const disp = (wrinkle * W) / 127;
  const pad = Math.ceil(wrinkle * W) + 1;
  const d = base.data, lmid = maps.lmid, hiDen = Math.max(1e-6, 1 - lmid);
  const tex = texture * look.texture;
  /** One print, composited over whatever is already in `base` — so overlapping areas layer in the
   *  order they are drawn. Returns whether it ran out of its area. */
  const draw = (area: PrintArea, placement: Placement, deg: number, a: RgbaImage): boolean => {
    const rect = artRectIn(area, placement, a);
    const clipped = printClipped(area, rect, deg);
    const Hi = quadToSquare(area.quad);
    // Inlined applyH(Hi, x, y): w = h6·x + h7·y + h8; u = (h0·x+h1·y+h2)/w; v = (h3·x+h4·y+h5)/w — avoids a per-pixel tuple allocation.
    const h0 = Hi[0], h1 = Hi[1], h2 = Hi[2], h3 = Hi[3], h4 = Hi[4], h5 = Hi[5], h6 = Hi[6], h7 = Hi[7], h8 = Hi[8];
    const b = quadBounds(area.quad);
    const aw = area.widthIn, ah = area.heightIn;
    // Rotation is the INVERSE turn about the art rect's centre: a pixel asks which part of the art
    // now covers it. cos/sin are hoisted per print, and θ = 0 keeps the original straight path —
    // cos(0)/sin(0) are exact, but `cx + (xIn − cx)` is not `xIn` in floating point.
    const rot = deg !== 0;
    const th = (deg * Math.PI) / 180, cos = Math.cos(th), sin = Math.sin(th);
    const cx = rect.x0 + rect.w / 2, cy = rect.y0 + rect.h / 2;
    for (let y = Math.max(0, b.y0 - pad); y < Math.min(H, b.y1 + pad); y++) {
      for (let x = Math.max(0, b.x0 - pad); x < Math.min(W, b.x1 + pad); x++) {
        const i = y * W + x, m = maps.mask[i];
        if (!m) continue;
        const px = x + 0.5 + maps.gx[i] * disp, py = y + 0.5 + maps.gy[i] * disp;
        const hw = h6 * px + h7 * py + h8;
        const u = (h0 * px + h1 * py + h2) / hw, v = (h3 * px + h4 * py + h5) / hw;
        if (u < 0 || u > 1 || v < 0 || v > 1) continue;
        let ax: number, ay: number;
        if (rot) {
          const dx = u * aw - cx, dy = v * ah - cy;
          const rx = cx + dx * cos + dy * sin, ry = cy - dx * sin + dy * cos;
          ax = ((rx - rect.x0) / rect.w) * a.width; ay = ((ry - rect.y0) / rect.h) * a.height;
        } else {
          ax = ((u * aw - rect.x0) / rect.w) * a.width; ay = ((v * ah - rect.y0) / rect.h) * a.height;
        }
        if (ax < 0 || ay < 0 || ax >= a.width || ay >= a.height) continue;
        const [cr, cg, cb, ca] = sampleArt(a, ax, ay);
        if (ca <= 0) continue;
        const l = maps.lumaBlur[i] / lmid;
        const shadow = l < 1 ? l : 1;
        const highlight = Math.min(1, Math.max(0, (maps.lumaBlur[i] - lmid) / hiDen));
        const sf = 1 - look.shadowDepth + look.shadowDepth * shadow, hf = 1 - highlight * look.highlight;
        const o = i * 4, r3 = i * 3;
        const k = ca * look.opacity * (m / 255);
        const c0 = Math.min(1, Math.max(0, 1 - (1 - cr * sf) * hf + (maps.residual[r3] / 255) * tex));
        const c1 = Math.min(1, Math.max(0, 1 - (1 - cg * sf) * hf + (maps.residual[r3 + 1] / 255) * tex));
        const c2 = Math.min(1, Math.max(0, 1 - (1 - cb * sf) * hf + (maps.residual[r3 + 2] / 255) * tex));
        d[o] = Math.round(d[o] * (1 - k) + c0 * 255 * k);
        d[o + 1] = Math.round(d[o + 1] * (1 - k) + c1 * 255 * k);
        d[o + 2] = Math.round(d[o + 2] * (1 - k) + c2 * 255 * k);
      }
    }
    return clipped;
  };
  let clipped = draw(template.printArea, job.placement, rotationDeg, art);
  // An extra print the job cannot honour — an area this blank does not have, or art that is not
  // open — is reported, never thrown: the other prints still belong on the garment. De-duplicated
  // by area, because this list is what the panel shows the shop: two prints that both failed in
  // the sleeve are one thing to tell them about, not two.
  const skipped: string[] = [];
  const skip = (areaId: string) => { if (!skipped.includes(areaId)) skipped.push(areaId); };
  for (const p of job.extraPrints ?? []) {
    if (!p || !p.enabled) continue;
    const area = areaById(template, p.areaId);
    if (!area) { skip(p.areaId); continue; }
    const pa = extraArts?.[p.docId ?? 'self'] ?? (p.docId === null ? art : undefined);
    if (!pa) { skip(p.areaId); continue; }
    const deg = p.rotationDeg ?? 0;
    checkPrint(p.placement, deg);
    if (draw(area, p.placement, deg, pa)) clipped = true;
  }
  return { image: base, clipped, skipped };
}
